import React, { useState, useEffect } from 'react'
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useToast } from '@/hooks/use-toast'
import { billingService } from '@/services/billingService'
import { StripeProvider } from './StripeProvider'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Loader2, CreditCard } from 'lucide-react'

const UpdatePaymentMethodForm: React.FC<{
  clinicId: string
  clientSecret: string
  onSuccess?: () => void
  onCancel?: () => void
}> = ({ clinicId, clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe()
  const elements = useElements()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setIsLoading(true)
    try {
      const { setupIntent, error: confirmError } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      })

      if (confirmError) {
        toast({
          title: 'Card Error',
          description: confirmError.message || 'Failed to process card',
          variant: 'destructive',
        })
        return
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : (setupIntent?.payment_method as { id?: string })?.id

      if (!paymentMethodId) {
        toast({
          title: 'Error',
          description: 'No payment method returned',
          variant: 'destructive',
        })
        return
      }

      const result = await billingService.updatePaymentMethod(clinicId, paymentMethodId)

      if (result.success) {
        toast({
          title: 'Card Updated',
          description: 'Your payment method has been updated successfully.',
        })
        onSuccess?.()
      } else {
        toast({
          title: 'Update Failed',
          description: result.error || 'Failed to update payment method',
          variant: 'destructive',
        })
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="text-base">Update Payment Method</CardTitle>
        <CardDescription>Enter your new card details. This will replace your current payment method.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-lg border border-slate-200 p-4">
            <PaymentElement options={{ layout: 'tabs' }} />
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={isLoading || !stripe}
              className="bg-brand-teal hover:bg-brand-teal/90"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Update Card
                </>
              )}
            </Button>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

interface UpdatePaymentMethodCardProps {
  clinicId: string
  onSuccess?: () => void
  onCancel?: () => void
}

export const UpdatePaymentMethodCard: React.FC<UpdatePaymentMethodCardProps> = ({
  clinicId,
  onSuccess,
  onCancel,
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [isInitLoading, setIsInitLoading] = useState(true)

  useEffect(() => {
    if (!clinicId) return

    const init = async () => {
      try {
        const result = await billingService.createSetupIntent(clinicId)
        if (result?.clientSecret) {
          setClientSecret(result.clientSecret)
        }
      } catch (err) {
        console.error('[UpdatePaymentMethodCard] Failed to create setup intent:', err)
      } finally {
        setIsInitLoading(false)
      }
    }
    init()
  }, [clinicId])

  if (isInitLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
      </div>
    )
  }

  if (!clientSecret) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-8 text-center">
          <p className="text-sm text-slate-500">Unable to initialize payment form. Please try again.</p>
          {onCancel && (
            <Button variant="outline" className="mt-4" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <StripeProvider clientSecret={clientSecret}>
      <UpdatePaymentMethodForm
        clinicId={clinicId}
        clientSecret={clientSecret}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </StripeProvider>
  )
}

export default UpdatePaymentMethodCard
